import React, {useState} from 'react';
import PropTypes from 'prop-types';
import {FormattedMessage, useIntl} from 'react-intl';

import {AuthHelpers, useAuthHelper, useCustomerType} from '@salesforce/commerce-sdk-react';
import {AccountIcon} from '@salesforce/retail-react-app/app/components/icons';
import {ensureArray, getCustomerInitials} from '@amblique/king-living/app/utils/utils';
import Link from '@salesforce/retail-react-app/app/components/link';
import {
  Box, Button, Flex, IconButton, Spinner, Stack, Text, useMultiStyleConfig,
} from '@salesforce/retail-react-app/app/components/shared/ui';
import withRegistration from '@salesforce/retail-react-app/app/components/with-registration';
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer';
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site';
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation';

import {BookmarkIcon, ChevronRightIcon2, LeftArrowIcon, LogoutIcon} from '@amblique/king-living/app/components/icons';
import LocaleSelector from '@amblique/king-living/app/components/locale-selector';
import {isHeaderWishlistIconEnabled} from '@amblique/king-living/app/constants/header';

const ButtonWithRegistration = withRegistration(Button);

const hasSubmenu = (menuItem) => {
  return ensureArray(menuItem?.submenuCollection?.items).filter(Boolean).length > 0;
};

export const MegaMenuPanel = ({
  category,
  menuItems = [],
  onBackClick,
  onArrowClick,
  onMyAccountClick,
  onMyQuoteClick,
  onSignInNavigate,
  parentCategory,
  showFunctionalSections = true,
  transform = 'translateX(0)',
  zIndex = 1,
  ...props
}) => {
  const intl = useIntl();
  const {isRegistered} = useCustomerType();
  const {data: customer, isError: isCustomerError, isFetched: isCustomerFetched, isLoading: isCustomerLoading} = useCurrentCustomer();
  const logout = useAuthHelper(AuthHelpers.Logout);
  const navigate = useNavigation();
  const {site, buildUrl} = useMultiSite();
  const showWishlistIcon = isHeaderWishlistIconEnabled();
  const [isSigningOut, setIsSigningOut] = useState(false);

  const handleSignOut = async () => {
    if (isSigningOut) return;
    setIsSigningOut(true);
    try {
      await logout.mutateAsync();
      navigate(buildUrl('/'), 'replace');
    } finally {
      setIsSigningOut(false);
    }
  };

  const handleSignInClick = () => {
    onSignInNavigate?.();
  };

  const handleArrowClick = (e, menuItem) => {
    e.preventDefault();
    e.stopPropagation();
    if (hasSubmenu(menuItem) && onArrowClick) {
      onArrowClick(menuItem);
    }
  };

  const styles = useMultiStyleConfig('MegaMenu', {});
  const firstCharsName = getCustomerInitials(customer);
  const isAccountAvatarLoading = isRegistered &&
    !firstCharsName &&
    !isCustomerError &&
    (isCustomerLoading || !isCustomerFetched);

  return (
    <Box
      className="mega-menu-panel__mobile"
      {...styles.mobileSubcategoryPanel}
      transform={transform}
      zIndex={zIndex}
      {...props}
      sx={{
        willChange: 'transform',
        ...props.sx,
      }}
    >
      <Box className="mega-menu-panel-content" {...styles.mobileSubcategoryPanelContent}>
        {onBackClick && (
          <Box
            as="button"
            className="mega-menu-panel-back-button"
            type="button"
            onClick={onBackClick}
            {...styles.backButtonWrapper}
          >
            <LeftArrowIcon {...styles.backButtonIcon} />
            <Text as="span" {...styles.backButton}>
              {parentCategory?.title || (
                <FormattedMessage defaultMessage="Back" id="mega_menu.back_button" />
              )}
            </Text>
          </Box>
        )}
        {category && category.link && (
          <Box mb={4}>
            <Link
              target={category.link.openInNewTab ? '_blank' : undefined}
              to={category.link.url || '#'}
            >
              <Text fontSize="sm" fontWeight="semibold">
                <FormattedMessage defaultMessage="Shop All" id="mega_menu.shop_all_button" /> {category.title}
              </Text>
            </Link>
          </Box>
        )}
        <Stack className="mega-menu-items-list mega-menu-items-list__mobile" spacing={0} {...styles.mobilePanelItemsList}>
          {ensureArray(menuItems).filter(Boolean).map((item) => {
            const hasSubmenuItems = hasSubmenu(item);
            const itemLink = item?.link;

            return (
              <Box key={item.sys?.id || item.title} className="mega-menu-item-parent-wrapper">
                {hasSubmenuItems ? (
                  <Flex
                    as={itemLink ? Link : 'div'}
                    className="mega-menu-item-parent__item"
                    target={itemLink?.openInNewTab ? '_blank' : undefined}
                    to={itemLink?.url}
                    onClick={(e) => handleArrowClick(e, item)}
                    {...styles.mobileMenuItemParentWrapper}
                  >
                    <Text className="mega-menu-item-text" {...styles.menuItemText}>
                      {item.title}
                    </Text>
                    <Box
                      as="button"
                      className="mega-menu-item-icon-button"
                      {...styles.menuItemIconButton}
                    >
                      <ChevronRightIcon2 {...styles.menuItemIcon} />
                    </Box>
                  </Flex>
                ) : itemLink ? (
                  <Link
                    className="mega-menu-item-parent__item"
                    target={itemLink.openInNewTab ? '_blank' : undefined}
                    to={itemLink.url || '#'}
                    width="100%"
                    {...styles.mobileMenuItemParentWrapper}
                  >
                    <Text className="mega-menu-item-text" {...styles.menuItemText}>
                      {item.title}
                    </Text>
                  </Link>
                ) : (
                  <Text className="mega-menu-item-parent__item mega-menu-item-text" {...styles.menuItemText}>
                    {item.title}
                  </Text>
                )}
              </Box>
            );
          })}
        </Stack>
      </Box>
      {showFunctionalSections && (
        <Box
          {...styles.mobileDrawerFooter}
          className="drawer-menu-footer"
        >
          <Stack {...styles.mobileDrawerFooterStack} gap="16px">
            <Box {...styles.mobileDrawerFooterRow} {...styles.mobileDrawerLocaleSelector}>
              <LocaleSelector variant="mobile" />
            </Box>
            <Box {...styles.mobileDrawerFooterRow}>
              {isRegistered ? (
                <ButtonWithRegistration
                  className="account-name-badge-button"
                  variant="unstyled"
                  aria-label={intl.formatMessage({
                    id: 'global.account.link.my_account',
                    defaultMessage: 'My account',
                  })}
                  {...styles.accountNameBadge}
                  {...styles.accountNameBadgeNotLoggedIn}
                  leftIcon={(<Box className="account-name-badge" {...styles.accountNameBadge} {...styles.mobileDrawerFooterIconButton}>
                    {isAccountAvatarLoading ? (
                      <Spinner aria-hidden color="currentColor" size="xs" thickness="1.5px" />
                    ) : (
                      firstCharsName
                    )}
                  </Box>)}
                  onClick={onMyAccountClick}
                >
                  <Text as="span" className="mobile-drawer-footer-text">
                    <FormattedMessage defaultMessage="My account" id="header.button.assistive_msg.my_account" />
                  </Text>
                </ButtonWithRegistration>
              ) : onSignInNavigate ? (
                <Link
                  className="account-name-badge-button"
                  to="/login"
                  aria-label={intl.formatMessage({
                    id: 'global.account.link.my_account',
                    defaultMessage: 'My account',
                  })}
                  {...styles.accountNameBadge}
                  {...styles.accountNameBadgeNotLoggedIn}
                  onClick={handleSignInClick}
                >
                  <AccountIcon />
                  <Text as="span" className="mobile-drawer-footer-text">
                    <FormattedMessage defaultMessage="Sign in" id="header.button.assistive_msg.sign_in" />
                  </Text>
                </Link>
              ) : (
                <ButtonWithRegistration
                  className="account-name-badge-button"
                  leftIcon={<AccountIcon />}
                  variant="unstyled"
                  aria-label={intl.formatMessage({
                    id: 'global.account.link.my_account',
                    defaultMessage: 'My account',
                  })}
                  {...styles.accountNameBadge}
                  {...styles.accountNameBadgeNotLoggedIn}
                  onClick={onMyAccountClick}
                >
                  <Text as="span" className="mobile-drawer-footer-text">
                    <FormattedMessage defaultMessage="Sign in" id="header.button.assistive_msg.sign_in" />
                  </Text>
                </ButtonWithRegistration>
              )}
            </Box>
            {showWishlistIcon && (
              <Box {...styles.mobileDrawerFooterRow}>
                <IconButton
                  icon={<BookmarkIcon />}
                  variant="unstyled"
                  aria-label={intl.formatMessage({
                    id: 'mega_menu.my_quote_button',
                    defaultMessage: 'My quote',
                  })}
                  onClick={onMyQuoteClick}
                  {...styles.mobileDrawerFooterIconButton}
                />
                <Text as="span" className="mobile-drawer-footer-text">
                  <FormattedMessage defaultMessage="My quote" id="mega_menu.my_quote_button" />
                </Text>
              </Box>
            )}
            {isRegistered && (
              <Box {...styles.mobileDrawerFooterRow}>
                <Box as="button"
                  variant="unstyled"
                  aria-label={intl.formatMessage({
                    defaultMessage: 'Log out',
                    id: 'header.popover.action.log_out',
                  })}
                  {...styles.accountNameBadge}
                  {...styles.accountNameBadgeNotLoggedIn}
                  onClick={handleSignOut}
                >
                  <LogoutIcon />
                  <Text as="span" className="mobile-drawer-footer-text">
                    <FormattedMessage
                      defaultMessage="Sign out"
                      id="header.popover.action.sign_out"
                    />
                  </Text>
                </Box>
              </Box>
            )}
          </Stack>
        </Box>
      )}
    </Box>
  );
};

MegaMenuPanel.displayName = 'MegaMenuPanel';
MegaMenuPanel.propTypes = {
  category: PropTypes.object,
  menuItems: PropTypes.array,
  onBackClick: PropTypes.func,
  onArrowClick: PropTypes.func,
  onMyAccountClick: PropTypes.func,
  onMyQuoteClick: PropTypes.func,
  onSignInNavigate: PropTypes.func,
  parentCategory: PropTypes.object,
  showFunctionalSections: PropTypes.bool,
  transform: PropTypes.string,
  zIndex: PropTypes.number,
  sx: PropTypes.object,
};
